/* eslint-disable */
import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Delete,
  CheckCircle,
  Error as ErrorIcon,
  ExpandMore,
  Folder,
  Article,
  Link as LinkIcon,
  OpenInNew,
  Edit,
  Close,
} from '@mui/icons-material';
import {
  commitDeliveryDraft,
  createDeliveryDraft,
  getFixVersions,
  getSwTeams,
  getSRDetails,
  updateDeliveryDraft,
  validateDeliveryDraft,
  getSrDashboard,
  getDeliveryTickets,
  getAssignees,
  getProductScopes,
  deleteTicket,
} from '../services/deliveryDraftApi';
import type {
  CommitErrorResponse,
  CommitResponse,
  DeliveryDraftResponse,
  DraftEpicInput,
  DraftStoryInput,
  DraftValidationResponse,
  SRDetailsResponse,
  SRTicketSummary,
  SRDashboardResponse,
  DeliveryTicketsResponse,
  DeliveryTicketEpic,
  DeliveryTicketStory,
  AssigneeUser,
} from '../types/deliveryDraft';

const buildStory = (
  order: number,
  defaults?: {
    fix_version?: string;
    product_scope?: string;
    sw_team?: string;
    assignee_account_id?: string;
    assignee_display_name?: string;
    jira_key?: string;
    source?: string;
  }
): DraftStoryInput => ({
  title: `Story ${order + 1}`,
  description: 'Describe the story',
  order,
  fix_version: defaults?.fix_version || '',
  product_scope: defaults?.product_scope || '',
  sw_team: defaults?.sw_team || '',
  assignee_account_id: defaults?.assignee_account_id || '',
  assignee_display_name: defaults?.assignee_display_name || '',
  jira_key: defaults?.jira_key || '',
  source: defaults?.source || 'draft',
});

const buildEpic = (
  order: number,
  defaults?: {
    fix_version?: string;
    product_scope?: string;
    assignee_account_id?: string;
    assignee_display_name?: string;
    jira_key?: string;
    source?: string;
  }
): DraftEpicInput => ({
  title: `Epic ${order + 1}`,
  description: 'Describe the epic',
  order,
  stories: [],
  fix_version: defaults?.fix_version || '',
  product_scope: defaults?.product_scope || '',
  assignee_account_id: defaults?.assignee_account_id || '',
  assignee_display_name: defaults?.assignee_display_name || '',
  jira_key: defaults?.jira_key || '',
  source: defaults?.source || 'draft',
});

const normalizeOrders = <T extends { order: number }>(items: T[]): T[] =>
  items.map((item, index) => ({ ...item, order: index }));

// Tech owner mapping from Product X Tech Scope to Architect names
const TECH_OWNER_MAP: Record<string, string> = {
  'Perception for People Flow Monitoring': 'Yann Briancon',
  'Real-Time Perception': 'Paul Edouard',
  'Lidar Integration': 'Clément Péron',
  'Real-time Analytics People & Object': 'Yann Briancon',
  'Real-time Analytics - Mixed environment': 'Yann Briancon',
  'Platform Tools & Enablers': 'Paul Edouard',
  'Platform Authentication & Access': 'Clément Péron',
  'Augmented Lidar Engine': 'Clément Péron',
  'Simulation - 3D simulator': 'Paul Edouard',
  'Support Portal (Outsight Cloud, Simulations, Documentation)': 'Paul Edouard',
};

export const ProductDeliveryPage: React.FC = () => {
  const theme = useTheme();
  const [srDashboard, setSrDashboard] = useState<SRDashboardResponse | null>(null);
  const [srDashboardLoading, setSrDashboardLoading] = useState(false);
  const [srDashboardError, setSrDashboardError] = useState<string | null>(null);
  const [findQuery, setFindQuery] = useState('');
  
  const [existingTickets, setExistingTickets] = useState<DeliveryTicketsResponse | null>(null);
  
  const [assigneeOptions, setAssigneeOptions] = useState<AssigneeUser[]>([]);
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState<Record<string, string>>({});
  const [assigneeSearchLoading, setAssigneeSearchLoading] = useState<Record<string, boolean>>({});

  const [selectedSR, setSelectedSR] = useState<SRTicketSummary | null>(null);
  const [srDetails, setSrDetails] = useState<SRDetailsResponse | null>(null);
  const [srDetailsLoading, setSrDetailsLoading] = useState(false);
  const [draft, setDraft] = useState<DeliveryDraftResponse | null>(null);
  const [draftLoading, setDraftLoading] = useState(false);
  const [epics, setEpics] = useState<DraftEpicInput[]>([]);
  const [standaloneStories, setStandaloneStories] = useState<DraftStoryInput[]>([]);
  const [defaultFixVersion, setDefaultFixVersion] = useState<string>('');
  const [draftError, setDraftError] = useState<string | null>(null);

  const [fixVersionOptions, setFixVersionOptions] = useState<string[]>([]);
  const [fixVersionsLoading, setFixVersionsLoading] = useState(false);
  const [fixVersionsError, setFixVersionsError] = useState<string | null>(null);

  const [swTeamOptions, setSwTeamOptions] = useState<string[]>([]);
  const [swTeamsLoading, setSwTeamsLoading] = useState(false);
  const [swTeamsError, setSwTeamsError] = useState<string | null>(null);

  const [productScopeOptions, setProductScopeOptions] = useState<string[]>([]);
  const [productScopesLoading, setProductScopesLoading] = useState(false);

  const [validationResult, setValidationResult] = useState<DraftValidationResponse | null>(null);
  const [commitResult, setCommitResult] = useState<CommitResponse | CommitErrorResponse | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCommitting, setIsCommitting] = useState(false);
  const [isValidating, setIsValidating] = useState(false);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<{
    key: string;
    type: 'epic' | 'story';
    hasChildren: boolean;
    title: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const validationTimeoutRef = React.useRef<number | null>(null);
  const validationSeqRef = React.useRef(0);

  const defaultProductScope = srDetails?.product_scope_candidates?.[0] || productScopeOptions[0] || '';
  const productScopeOptionsForStories = useMemo(
    () => [...new Set([...productScopeOptions, ...(srDetails?.product_scope_candidates || [])])].sort(),
    [productScopeOptions, srDetails?.product_scope_candidates]
  );
  const commitErrors = commitResult && 'errors' in commitResult ? commitResult.errors : [];

  const totalEpicStories = useMemo(
    () => epics.reduce((count, epic) => count + epic.stories.length, 0),
    [epics]
  );

  const buildDraftUpdatePayload = React.useCallback(() => {
    return {
      epics: normalizeOrders(epics).map((epic) => ({
        ...epic,
        stories: normalizeOrders(epic.stories),
      })),
      standalone_stories: normalizeOrders(standaloneStories),
      default_fix_version: defaultFixVersion || null,
    };
  }, [epics, standaloneStories, defaultFixVersion]);

  const validationIndex = useMemo(() => {
    const map = new Map<string, { error?: string; warning?: string }>();
    if (!validationResult) return map;

    for (const issue of validationResult.warnings || []) {
      const key = `${issue.item_path || ''}::${issue.field || ''}`;
      const existing = map.get(key) || {};
      if (!existing.warning) existing.warning = issue.message;
      map.set(key, existing);
    }
    for (const issue of validationResult.errors || []) {
      const key = `${issue.item_path || ''}::${issue.field || ''}`;
      const existing = map.get(key) || {};
      if (!existing.error) existing.error = issue.message;
      map.set(key, existing);
    }

    return map;
  }, [validationResult]);

  const getFieldIssue = React.useCallback(
    (itemPath: string | null | undefined, field: string | null | undefined) => {
      const key = `${itemPath || ''}::${field || ''}`;
      return validationIndex.get(key);
    },
    [validationIndex]
  );

  // Load SR dashboard and product scopes on mount
  React.useEffect(() => {
    const loadDashboard = async () => {
      setSrDashboardLoading(true);
      setSrDashboardError(null);
      try {
        const result = await getSrDashboard();
        setSrDashboard(result);
      } catch (error) {
        setSrDashboardError(error instanceof Error ? error.message : 'Failed to load SR dashboard');
      } finally {
        setSrDashboardLoading(false);
      }
    };
    loadDashboard();
  }, []);

  // Load product scopes on mount so dropdown has options even if SR selection fails
  React.useEffect(() => {
    const loadProductScopes = async () => {
      setProductScopesLoading(true);
      try {
        const result = await getProductScopes(undefined, 'Story');
        setProductScopeOptions(result.scopes || []);
      } catch (error) {
        console.error('Failed to load product scopes:', error);
      } finally {
        setProductScopesLoading(false);
      }
    };
    loadProductScopes();
  }, []);

  // Live (debounced) commit-time validation: update server draft, then validate.
  React.useEffect(() => {
    if (!draft) return;
    if (isSaving || isCommitting) return;

    const hasAnyItems = epics.length > 0 || standaloneStories.length > 0;
    if (!hasAnyItems) {
      setValidationResult(null);
      return;
    }

    if (validationTimeoutRef.current) {
      window.clearTimeout(validationTimeoutRef.current);
      validationTimeoutRef.current = null;
    }

    const seq = ++validationSeqRef.current;
    validationTimeoutRef.current = window.setTimeout(async () => {
      setIsValidating(true);
      try {
        await updateDeliveryDraft(draft.draft_id, buildDraftUpdatePayload());
        const result = await validateDeliveryDraft(draft.draft_id, true);
        if (validationSeqRef.current === seq) {
          setValidationResult(result);
        }
      } catch (error) {
        if (validationSeqRef.current === seq) {
          console.error('Live validation failed:', error);
        }
      } finally {
        if (validationSeqRef.current === seq) {
          setIsValidating(false);
        }
      }
    }, 600);

    return () => {
      if (validationTimeoutRef.current) {
        window.clearTimeout(validationTimeoutRef.current);
        validationTimeoutRef.current = null;
      }
    };
  }, [
    draft,
    epics,
    standaloneStories,
    buildDraftUpdatePayload,
    isSaving,
    isCommitting,
    validationTimeoutRef,
  ]);

  // Filter SRs by find query
  const filteredSections = React.useMemo(() => {
    if (!srDashboard || !findQuery.trim()) {
      return srDashboard?.sections || [];
    }
    const query = findQuery.toLowerCase();
    return srDashboard.sections.map((section) => ({
      ...section,
      srs: section.srs.filter(
        (sr) =>
          sr.key.toLowerCase().includes(query) ||
          sr.summary.toLowerCase().includes(query) ||
          sr.area.some((a) => a.toLowerCase().includes(query))
      ),
    })).filter((section) => section.srs.length > 0);
  }, [srDashboard, findQuery]);

  // Search assignees
  const handleSearchAssignees = async (storyKey: string, query: string) => {
    if (!query.trim() || query.length < 2) {
      setAssigneeOptions([]);
      return;
    }
    setAssigneeSearchLoading((prev) => ({ ...prev, [storyKey]: true }));
    try {
      const result = await getAssignees(query);
      setAssigneeOptions(result.users);
    } catch (error) {
      console.error('Failed to search assignees:', error);
    } finally {
      setAssigneeSearchLoading((prev) => ({ ...prev, [storyKey]: false }));
    }
  };

  const handleSelectSR = async (ticket: SRTicketSummary) => {
    setSelectedSR(ticket);
    setDraftError(null);
    setValidationResult(null);
    setCommitResult(null);
    setDraft(null);
    setEpics([]);
    setStandaloneStories([]);
    setDefaultFixVersion('');
    setExistingTickets(null);
    setSrDetails(null);
    setSrDetailsLoading(true);
    setDraftLoading(true);
    try {
      setFixVersionsError(null);
      setFixVersionsLoading(true);
      setSwTeamsError(null);
      setSwTeamsLoading(true);
      setProductScopesLoading(true);
      
      const [details, fixVersions, swTeams, productScopes, deliveryTickets] = await Promise.all([
        getSRDetails(ticket.key),
        getFixVersions(),
        getSwTeams(undefined, 'Story'),
        getProductScopes(undefined, 'Story'),
        getDeliveryTickets(ticket.key),
      ]);
      
      setSrDetails(details);
      setFixVersionOptions(fixVersions.versions || []);
      setSwTeamOptions(swTeams.teams || []);
      setProductScopeOptions(productScopes.scopes || []);
      setExistingTickets(deliveryTickets);
      setSrDetailsLoading(false);
      
      // IMPORTANT: existing delivery tickets are displayed read-only.
      // Do not inject them into the draft builder — step 3 must only offer "Add" actions.
      const createdDraft = await createDeliveryDraft({
        sr_key: ticket.key,
        epics: [],
        standalone_stories: [],
        default_fix_version: '',
        product_scope_candidates: details.product_scope_candidates,
      });
      setDraft(createdDraft);
      setEpics([]);
      setStandaloneStories([]);
      setDraftLoading(false);
    } catch (error) {
      setDraftError(error instanceof Error ? error.message : 'Failed to create draft');
      setFixVersionsError(error instanceof Error ? error.message : 'Failed to load Fix Versions');
      setSwTeamsError(error instanceof Error ? error.message : 'Failed to load SW Teams');
      setSrDetailsLoading(false);
      setDraftLoading(false);
    } finally {
      setFixVersionsLoading(false);
      setSwTeamsLoading(false);
      setProductScopesLoading(false);
    }
  };

  const handleAddEpic = () => {
    setEpics((prev) => [
      ...prev,
      buildEpic(prev.length, {
        fix_version: defaultFixVersion,
        product_scope: defaultProductScope,
      }),
    ]);
  };

  const handleAddStory = () => {
    const techOwner = defaultProductScope ? TECH_OWNER_MAP[defaultProductScope] : undefined;
    setStandaloneStories((prev) => [
      ...prev,
      buildStory(prev.length, {
        fix_version: defaultFixVersion,
        product_scope: defaultProductScope,
        assignee_display_name: techOwner,
      }),
    ]);
  };

  const handleAddStoryToEpic = (epicIndex: number) => {
    setEpics((prev) =>
      prev.map((epic, index) => {
        if (index !== epicIndex) return epic;
        const scope = epic.product_scope || defaultProductScope;
        const techOwner = scope ? TECH_OWNER_MAP[scope] : undefined;
        return {
          ...epic,
          stories: [
            ...epic.stories,
            buildStory(epic.stories.length, {
              fix_version: epic.fix_version || defaultFixVersion,
              product_scope: scope,
              assignee_display_name: techOwner,
            }),
          ],
        };
      })
    );
  };

  const handleMoveItem = <T,>(
    items: T[],
    setItems: React.Dispatch<React.SetStateAction<T[]>>,
    index: number,
    direction: 'up' | 'down'
  ) => {
    const nextIndex = direction === 'up' ? index - 1 : index + 1;
    if (nextIndex < 0 || nextIndex >= items.length) return;
    const updated = [...items];
    [updated[index], updated[nextIndex]] = [updated[nextIndex], updated[index]];
    setItems(updated);
  };

  const handleEditExistingTicket = (
    ticket: DeliveryTicketEpic | DeliveryTicketStory,
    type: 'epic' | 'story',
    parentEpicKey?: string
  ) => {
    if (type === 'epic') {
      const epicTicket = ticket as DeliveryTicketEpic;
      // Check if already in draft builder
      if (epics.some(e => e.jira_key === epicTicket.jira_key)) {
        return; // Already editing
      }
      
      // Convert to draft format
      const draftEpic: DraftEpicInput = {
        title: epicTicket.title,
        description: epicTicket.description,
        order: epics.length,
        stories: (epicTicket.stories || []).map((story: DeliveryTicketStory, idx: number) => ({
          title: story.title,
          description: story.description,
          order: idx,
          fix_version: story.fix_version || '',
          product_scope: story.product_scope || '',
          sw_team: story.sw_team || '',
          assignee_account_id: story.assignee_account_id || '',
          assignee_display_name: story.assignee || '',
          jira_key: story.jira_key,
          source: 'jira',
        })),
        fix_version: epicTicket.fix_version || '',
        product_scope: epicTicket.product_scope || '',
        assignee_account_id: epicTicket.assignee_account_id || '',
        assignee_display_name: epicTicket.assignee || '',
        jira_key: epicTicket.jira_key,
        source: 'jira',
      };
      
      setEpics((prev) => [...prev, draftEpic]);
    } else {
      const storyTicket = ticket as DeliveryTicketStory;
      // Check if already in draft builder
      const isInEpic = epics.some(e => 
        e.stories.some(s => s.jira_key === storyTicket.jira_key)
      );
      const isInStandalone = standaloneStories.some(s => s.jira_key === storyTicket.jira_key);
      
      if (isInEpic || isInStandalone) {
        return; // Already editing
      }
      
      // Convert to draft format
      const draftStory: DraftStoryInput = {
        title: storyTicket.title,
        description: storyTicket.description,
        order: standaloneStories.length,
        fix_version: storyTicket.fix_version || '',
        product_scope: storyTicket.product_scope || '',
        sw_team: storyTicket.sw_team || '',
        assignee_account_id: storyTicket.assignee_account_id || '',
        assignee_display_name: storyTicket.assignee || '',
        jira_key: storyTicket.jira_key,
        source: 'jira',
      };
      
      // If story belongs to an epic, add to that epic's stories
      if (parentEpicKey) {
        const epicIndex = epics.findIndex(e => e.jira_key === parentEpicKey);
        if (epicIndex >= 0) {
          setEpics((prev) =>
            prev.map((epic, idx) =>
              idx === epicIndex
                ? {
                    ...epic,
                    stories: [...epic.stories, draftStory],
                  }
                : epic
            )
          );
          return;
        }
      }
      
      // Otherwise add as standalone story
      setStandaloneStories((prev) => [...prev, draftStory]);
    }
  };

  const handleCancelEdit = () => {
    // Remove all tickets that are being edited (have jira_key and source === 'jira')
    // This restores them to the preview pane as "existing" tickets
    setEpics((prev) => {
      // Filter out epics being edited, and also remove edited stories from remaining epics
      return prev
        .filter((epic) => !(epic.jira_key && epic.source === 'jira'))
        .map((epic) => ({
          ...epic,
          stories: epic.stories.filter((story) => !(story.jira_key && story.source === 'jira')),
        }));
    });
    setStandaloneStories((prev) => prev.filter((story) => !(story.jira_key && story.source === 'jira')));
    // Also clear any validation results since we're canceling edits
    setValidationResult(null);
    setDraftError(null);
  };

  const hasEditedTickets = useMemo(() => {
    return (
      epics.some((e) => e.jira_key && e.source === 'jira') ||
      standaloneStories.some((s) => s.jira_key && s.source === 'jira')
    );
  }, [epics, standaloneStories]);

  const handleSaveDraft = async () => {
    if (!draft) return;
    setIsSaving(true);
    setDraftError(null);
    setValidationResult(null);
    setCommitResult(null);
    try {
      const updated = await updateDeliveryDraft(draft.draft_id, {
        epics: normalizeOrders(epics).map((epic) => ({
          ...epic,
          stories: normalizeOrders(epic.stories),
        })),
        standalone_stories: normalizeOrders(standaloneStories),
        default_fix_version: defaultFixVersion || null,
      });
      setDraft(updated);
      setEpics(updated.epics);
      setStandaloneStories(updated.standalone_stories);
    } catch (error) {
      setDraftError(error instanceof Error ? error.message : 'Failed to save draft');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCommitDraft = async () => {
    if (!draft) return;
    setIsCommitting(true);
    setDraftError(null);
    setCommitResult(null);
    try {
      // Ensure server-side draft matches current UI state before committing.
      const updated = await updateDeliveryDraft(draft.draft_id, buildDraftUpdatePayload());
      setDraft(updated);
      setEpics(updated.epics);
      setStandaloneStories(updated.standalone_stories);

      const commitId = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const result = await commitDeliveryDraft(draft.draft_id, {
        client_commit_id: commitId,
      });
      setCommitResult(result);

      // On successful commit: refresh hierarchy (delivery tickets) and reset draft builder.
      if (result && 'success' in result && result.success && selectedSR) {
        try {
          const [deliveryTickets, refreshedDraft] = await Promise.all([
            getDeliveryTickets(selectedSR.key),
            createDeliveryDraft({
              sr_key: selectedSR.key,
              epics: [],
              standalone_stories: [],
              default_fix_version: '',
              product_scope_candidates: srDetails?.product_scope_candidates || [],
            }),
          ]);
          setExistingTickets(deliveryTickets);
          setDraft(refreshedDraft);
          setEpics([]);
          setStandaloneStories([]);
          setDefaultFixVersion('');
          setValidationResult(null);
          setAssigneeSearchQuery({});
          setAssigneeOptions([]);
        } catch (error) {
          console.error('Post-commit refresh failed:', error);
        }
      }
    } catch (error) {
      setDraftError(error instanceof Error ? error.message : 'Failed to commit draft');
    } finally {
      setIsCommitting(false);
    }
  };

  const handleDeleteClick = (
    ticket: DeliveryTicketEpic | DeliveryTicketStory,
    type: 'epic' | 'story',
    hasChildren: boolean = false
  ) => {
    setTicketToDelete({
      key: ticket.jira_key,
      type,
      hasChildren,
      title: ticket.title,
    });
    setDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!ticketToDelete || !selectedSR) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      await deleteTicket(ticketToDelete.key);
      
      // Refresh delivery tickets to update the preview pane
      const deliveryTickets = await getDeliveryTickets(selectedSR.key);
      setExistingTickets(deliveryTickets);
      
      // Close dialog and show success
      setDeleteDialogOpen(false);
      setTicketToDelete(null);
      
      // Show success message (could use a snackbar, but for now just clear error)
      setDeleteError(null);
    } catch (error) {
      setDeleteError(error instanceof Error ? error.message : 'Failed to delete ticket');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTicketToDelete(null);
    setDeleteError(null);
  };

  const renderStoryFields = (
    story: DraftStoryInput,
    onChange: (updated: DraftStoryInput) => void,
    storyKey?: string,
    itemPath?: string
  ) => {
    const key = storyKey || `${story.title}-${story.order}`;
    const assigneeQuery = assigneeSearchQuery[key] || '';
    const assigneeLoading = assigneeSearchLoading[key] || false;

    const titleIssue = getFieldIssue(itemPath, 'title');
    const descriptionIssue = getFieldIssue(itemPath, 'description');
    const fixVersionIssue = getFieldIssue(itemPath, 'fix_version');
    const productScopeIssue = getFieldIssue(itemPath, 'product_scope');
    const swTeamIssue = getFieldIssue(itemPath, 'sw_team');
    
    return (
      <Stack spacing={1.5} sx={{ mt: 0.5 }}>
        <TextField
          label="Title"
          value={story.title}
          onChange={(event) => onChange({ ...story, title: event.target.value })}
          fullWidth
          error={!!titleIssue?.error}
          helperText={titleIssue?.error || titleIssue?.warning || undefined}
        />
        <TextField
          label="Description"
          value={story.description}
          onChange={(event) => onChange({ ...story, description: event.target.value })}
          fullWidth
          multiline
          minRows={2}
          error={!!descriptionIssue?.error}
          helperText={descriptionIssue?.error || descriptionIssue?.warning || undefined}
        />
        <Autocomplete
          freeSolo
          options={fixVersionOptions}
          value={story.fix_version || ''}
          onChange={(_event, value) =>
            onChange({ ...story, fix_version: typeof value === 'string' ? value : (value || '') })
          }
          onInputChange={(_event, value) => onChange({ ...story, fix_version: value })}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Fix Version"
              fullWidth
              error={!!fixVersionIssue?.error}
              helperText={
                fixVersionIssue?.error ||
                fixVersionIssue?.warning ||
                (fixVersionsError
                  ? `Fix Versions: ${fixVersionsError}`
                  : 'Select an existing Fix Version or type a new one')
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {fixVersionsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        <Autocomplete
          freeSolo
          options={productScopeOptionsForStories}
          value={story.product_scope || ''}
          onChange={(_event, value) => {
            const newScope = typeof value === 'string' ? value : (value || '');
            const techOwner = newScope ? TECH_OWNER_MAP[newScope] : undefined;
            onChange({
              ...story,
              product_scope: newScope,
              assignee_display_name: techOwner || story.assignee_display_name || '',
            });
          }}
          onInputChange={(_event, value) => {
            onChange({ ...story, product_scope: value });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Product X Tech Scope"
              fullWidth
              error={!!productScopeIssue?.error}
              helperText={
                productScopeIssue?.error ||
                productScopeIssue?.warning ||
                'Select from suggestions or type a custom value'
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {productScopesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {swTeamOptions.length > 0 ? (
          <Autocomplete
            freeSolo
            options={swTeamOptions}
            value={story.sw_team || ''}
            onChange={(_event, value) =>
              onChange({ ...story, sw_team: typeof value === 'string' ? value : (value || '') })
            }
            onInputChange={(_event, value) => onChange({ ...story, sw_team: value })}
            renderInput={(params) => (
              <TextField
                {...params}
                label="SW Team"
                fullWidth
                error={!!swTeamIssue?.error}
                helperText={
                  swTeamIssue?.error ||
                  swTeamIssue?.warning ||
                  (swTeamsError ? `SW Teams: ${swTeamsError}` : 'Select an existing SW Team or type a new one')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {swTeamsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        ) : (
          <TextField
            label="SW Team"
            value={story.sw_team || ''}
            onChange={(event) => onChange({ ...story, sw_team: event.target.value })}
            fullWidth
            error={!!swTeamIssue?.error}
            helperText={
              swTeamIssue?.error ||
              swTeamIssue?.warning ||
              (swTeamsError ? `SW Teams: ${swTeamsError}` : undefined)
            }
          />
        )}
        <Autocomplete
          freeSolo
          options={assigneeOptions}
          value={story.assignee_display_name || ''}
          getOptionLabel={(option) => {
            if (typeof option === 'string') return option;
            return option.display_name || '';
          }}
          onChange={(_event, value) => {
            if (typeof value === 'string') {
              onChange({
                ...story,
                assignee_display_name: value,
                assignee_account_id: '',
              });
            } else if (value) {
              onChange({
                ...story,
                assignee_display_name: value.display_name,
                assignee_account_id: value.account_id || '',
              });
            }
          }}
          onInputChange={(_event, value) => {
            setAssigneeSearchQuery((prev) => ({ ...prev, [key]: value }));
            if (value.length >= 2) {
              handleSearchAssignees(key, value);
            }
            onChange({ ...story, assignee_display_name: value });
          }}
          inputValue={assigneeQuery}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assignee"
              fullWidth
              helperText="Type to search assignable users or enter manually"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {assigneeLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Stack>
    );
  };

  const JIRA_BASE_URL = 'https://outsight.atlassian.net';
  
  const buildJiraTicketUrl = (ticketKey: string): string => {
    return `${JIRA_BASE_URL}/browse/${ticketKey}`;
  };

  // Hierarchy preview component
  const renderHierarchyPreview = () => {
    if (!selectedSR) return null;
    
    if (srDetailsLoading || !srDetails) {
      return (
        <Card sx={{ height: '100%' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
              Hierarchy Preview
            </Typography>
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
              <CircularProgress />
            </Box>
          </CardContent>
        </Card>
      );
    }
    
    // Get keys of existing tickets being edited in draft builder
    const editingEpicKeys = new Set(
      epics.filter(e => e.source === 'jira' && e.jira_key).map(e => e.jira_key)
    );
    const editingStoryKeys = new Set([
      ...epics.flatMap(e => e.stories.filter(s => s.source === 'jira' && s.jira_key).map(s => s.jira_key)),
      ...standaloneStories.filter(s => s.source === 'jira' && s.jira_key).map(s => s.jira_key),
    ]);

    // Filter out existing tickets that are being edited (they'll appear as drafts instead)
    const allEpics = [
      ...(existingTickets?.epics || [])
        .filter((e) => !editingEpicKeys.has(e.jira_key))
        .map((e) => ({ ...e, isDraft: false as const })),
      ...epics.map((e, idx) => ({ ...e, isDraft: true as const, tempKey: `draft-${idx}` })),
    ];
    const allStandaloneStories = [
      ...(existingTickets?.standalone_stories || [])
        .filter((s) => !editingStoryKeys.has(s.jira_key))
        .map((s) => ({ ...s, isDraft: false as const })),
      ...standaloneStories.map((s, idx) => ({ ...s, isDraft: true as const, tempKey: `draft-${idx}` })),
    ];
    const hasAnyItems = allEpics.length > 0 || allStandaloneStories.length > 0;

    return (
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
            Hierarchy Preview
          </Typography>
          <Box sx={{ 
            border: '1px solid', 
            borderColor: 'divider', 
            borderRadius: 1, 
            p: 1.5, 
            bgcolor: 'background.paper',
            fontSize: '0.875rem'
          }}>
            <Stack spacing={1}>
              {/* SR Root */}
              <Box sx={{ pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <LinkIcon fontSize="small" color="primary" />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {srDetails.key}
                  </Typography>
                  <IconButton
                    size="small"
                    component="a"
                    href={buildJiraTicketUrl(srDetails.key)}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ p: 0.25 }}
                    aria-label={`Open ${srDetails.key} in Jira`}
                  >
                    <OpenInNew fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    — {srDetails.summary}
                  </Typography>
                </Stack>
              </Box>

              {!hasAnyItems ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 2 }}>
                  No delivery tickets yet
                </Typography>
              ) : (
                <>
                  {/* Epics */}
                  {allEpics.map((epic) => (
                    <Box
                      key={epic.isDraft ? (epic as any).tempKey : epic.jira_key}
                      sx={{
                        borderLeft: `3px solid ${epic.isDraft ? '#ff9800' : '#4caf50'}`,
                        pl: 1.5,
                        ml: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                        <Folder fontSize="small" color={epic.isDraft && epic.jira_key ? 'info' : (epic.isDraft ? 'warning' : 'success')} />
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {epic.isDraft && epic.jira_key ? epic.jira_key : (epic.isDraft ? epic.title : epic.jira_key)}
                        </Typography>
                        {epic.jira_key && (
                          <>
                            <IconButton
                              size="small"
                              component="a"
                              href={buildJiraTicketUrl(epic.jira_key || '')}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ p: 0.25 }}
                              aria-label={`Open ${epic.jira_key || ''} in Jira`}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                            {!epic.isDraft && (
                              <>
                                <Tooltip title="Edit in draft builder">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEditExistingTicket(epic, 'epic')}
                                      disabled={epics.some(e => e.jira_key === epic.jira_key)}
                                      sx={{ p: 0.25 }}
                                      aria-label={`Edit ${epic.jira_key || ''}`}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Delete ticket">
                                  <span>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteClick(epic, 'epic', (epic.stories || []).length > 0)}
                                      disabled={epics.some(e => e.jira_key === epic.jira_key)}
                                      sx={{ p: 0.25, color: 'error.main' }}
                                      aria-label={`Delete ${epic.jira_key || ''}`}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </>
                            )}
                          </>
                        )}
                        {epic.isDraft && epic.jira_key ? (
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            — {epic.title}
                          </Typography>
                        ) : !epic.isDraft && (
                          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            — {epic.title}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          label={epic.isDraft && epic.jira_key ? 'editing' : (epic.isDraft ? 'draft' : 'existing')}
                          color={epic.isDraft && epic.jira_key ? 'info' : (epic.isDraft ? 'warning' : 'success')}
                          sx={{ ml: 'auto', height: 20 }}
                        />
                      </Stack>
                      <Box sx={{ pl: 3, mt: 0.5 }}>
                        {(() => {
                          // Filter out stories being edited individually (only for existing epics)
                          const visibleStories = epic.isDraft
                            ? epic.stories
                            : (epic.stories || []).filter((s) => !editingStoryKeys.has(s.jira_key));
                          return visibleStories.length > 0 ? (
                          <Stack spacing={0.5}>
                            {visibleStories.map((story, sidx) => (
                              <Stack
                                key={story.jira_key || `draft-story-${sidx}`}
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                <Article fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
                                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                                  {story.jira_key ? (
                                    <>
                                      <strong>{story.jira_key}</strong>
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={buildJiraTicketUrl(story.jira_key)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ p: 0.25, ml: 0.5 }}
                                        aria-label={`Open ${story.jira_key} in Jira`}
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                      {!editingStoryKeys.has(story.jira_key) && (
                                        <>
                                          <Tooltip title="Edit in draft builder">
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleEditExistingTicket(story as DeliveryTicketStory, 'story', epic.jira_key || undefined)}
                                                disabled={
                                                  epics.some(e => 
                                                    e.stories.some(s => s.jira_key === story.jira_key)
                                                  ) || standaloneStories.some(s => s.jira_key === story.jira_key)
                                                }
                                                sx={{ p: 0.25, ml: 0.5 }}
                                                aria-label={`Edit ${story.jira_key || ''}`}
                                              >
                                                <Edit fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                          <Tooltip title="Delete ticket">
                                            <span>
                                              <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(story as DeliveryTicketStory, 'story', false)}
                                                disabled={
                                                  epics.some(e => 
                                                    e.stories.some(s => s.jira_key === story.jira_key)
                                                  ) || standaloneStories.some(s => s.jira_key === story.jira_key)
                                                }
                                                sx={{ p: 0.25, ml: 0.5, color: 'error.main' }}
                                                aria-label={`Delete ${story.jira_key || ''}`}
                                              >
                                                <Delete fontSize="small" />
                                              </IconButton>
                                            </span>
                                          </Tooltip>
                                        </>
                                      )}
                                      {' — '}
                                      {story.title}
                                      {editingStoryKeys.has(story.jira_key) && (
                                        <Chip size="small" label="editing" color="info" sx={{ ml: 0.5, height: 18 }} />
                                      )}
                                    </>
                                  ) : (
                                    <>
                                      <em>{story.title}</em>
                                      <Chip size="small" label="draft" sx={{ ml: 0.5, height: 18 }} />
                                    </>
                                  )}
                                </Typography>
                              </Stack>
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No stories
                          </Typography>
                        );
                        })()}
                      </Box>
                    </Box>
                  ))}

                  {/* Standalone Stories */}
                  {allStandaloneStories.map((story) => (
                    <Stack
                      key={story.isDraft ? (story as any).tempKey : story.jira_key}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        borderLeft: `3px solid ${story.isDraft ? '#ff9800' : '#4caf50'}`,
                        pl: 1.5,
                        ml: 1,
                      }}
                    >
                      <Article fontSize="small" color={story.isDraft && story.jira_key ? 'info' : (story.isDraft ? 'warning' : 'success')} />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {story.isDraft && story.jira_key ? (
                          <>
                            <strong>{story.jira_key}</strong>
                            <IconButton
                              size="small"
                              component="a"
                              href={buildJiraTicketUrl(story.jira_key)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ p: 0.25, ml: 0.5 }}
                              aria-label={`Open ${story.jira_key} in Jira`}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                            {' — '}
                            {story.title}
                          </>
                        ) : story.isDraft ? (
                          <em>{story.title}</em>
                        ) : (
                          <>
                            <strong>{story.jira_key}</strong>
                            <IconButton
                              size="small"
                              component="a"
                              href={buildJiraTicketUrl(story.jira_key)}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{ p: 0.25, ml: 0.5 }}
                              aria-label={`Open ${story.jira_key} in Jira`}
                            >
                              <OpenInNew fontSize="small" />
                            </IconButton>
                            <Tooltip title="Edit in draft builder">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditExistingTicket(story, 'story')}
                                  disabled={
                                    epics.some(e => 
                                      e.stories.some(s => s.jira_key === story.jira_key)
                                    ) || standaloneStories.some(s => s.jira_key === story.jira_key)
                                  }
                                  sx={{ p: 0.25, ml: 0.5 }}
                                  aria-label={`Edit ${story.jira_key}`}
                                >
                                  <Edit fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Delete ticket">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(story, 'story', false)}
                                  disabled={
                                    epics.some(e => 
                                      e.stories.some(s => s.jira_key === story.jira_key)
                                    ) || standaloneStories.some(s => s.jira_key === story.jira_key)
                                  }
                                  sx={{ p: 0.25, ml: 0.5, color: 'error.main' }}
                                  aria-label={`Delete ${story.jira_key}`}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            {' — '}
                            {story.title}
                          </>
                        )}
                      </Typography>
                      <Chip
                        size="small"
                        label={story.isDraft && story.jira_key ? 'editing' : (story.isDraft ? 'draft' : 'existing')}
                        color={story.isDraft && story.jira_key ? 'info' : (story.isDraft ? 'warning' : 'success')}
                        sx={{ height: 20 }}
                      />
                    </Stack>
                  ))}
                </>
              )}
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ pb: 3 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Product Delivery
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Build and validate SR delivery hierarchies before committing to Jira.
          </Typography>
        </Box>

        <Grid container spacing={2.5} alignItems="flex-start" sx={{ mx: 0, width: '100%' }}>
          <Grid item xs={12} md={selectedSR ? 5 : 6}>
          <Card>
            <CardContent sx={{ p: 2.5 }}>
              <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
                Select an SR
              </Typography>
              <Stack spacing={1.5}>
                <TextField
                  label="Find SR"
                  value={findQuery}
                  onChange={(event) => setFindQuery(event.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
                {srDashboardLoading && (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                  </Box>
                )}
                {srDashboardError && <Alert severity="error">{srDashboardError}</Alert>}
                {filteredSections.length > 0 && (
                  <Box sx={{ maxHeight: { md: 'calc(100vh - 340px)' }, overflow: 'auto' }}>
                    {filteredSections.map((section) => (
                      <Accordion key={section.expected_version} defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                          <Typography variant="subtitle1">
                            {section.expected_version} ({section.srs.length})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <List dense>
                            {section.srs.map((ticket) => (
                              <ListItem
                                key={ticket.key}
                                disableGutters
                                secondaryAction={(
                                  <Button size="small" variant="outlined" onClick={() => handleSelectSR(ticket)}>
                                    Select
                                  </Button>
                                )}
                              >
                                <ListItemText
                                  primary={
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <LinkIcon fontSize="small" color="primary" />
                                      <Typography component="span">{ticket.key}</Typography>
                                      <IconButton
                                        size="small"
                                        component="a"
                                        href={buildJiraTicketUrl(ticket.key)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{ p: 0.25 }}
                                        aria-label={`Open ${ticket.key} in Jira`}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <OpenInNew fontSize="small" />
                                      </IconButton>
                                      <Typography component="span" color="text.secondary">
                                        — {ticket.summary}
                                      </Typography>
                                    </Stack>
                                  }
                                  secondary={`Status: ${ticket.status} • Area: ${ticket.area.join(', ') || 'N/A'}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Box>
                )}
                {!srDashboardLoading && filteredSections.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No SRs found. {findQuery ? 'Try adjusting your search.' : 'Loading...'}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {selectedSR && (
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                position: { md: 'sticky' },
                top: { md: `calc(${theme.mixins.toolbar.minHeight}px + 16px)` },
                zIndex: { md: theme.zIndex.appBar - 2 },
              }}
            >
              <Stack spacing={2.5}>
                {renderHierarchyPreview()}
                
                {draftLoading && (
                  <Card>
                    <CardContent sx={{ p: 2.5 }}>
                      <Typography variant="h6" gutterBottom sx={{ mb: 1.5 }}>
                        Draft Builder
                      </Typography>
                      <Box display="flex" justifyContent="center" alignItems="center" sx={{ minHeight: 200 }}>
                        <CircularProgress />
                      </Box>
                    </CardContent>
                  </Card>
                )}
                
                {draft && !draftLoading && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Draft Builder
                  </Typography>
                  <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ mb: 1 }}>
                  Preview
                </Typography>
                <Stack direction="row" spacing={1.5}>
                  <Chip label={`Epics: ${epics.length}`} />
                  <Chip label={`Epic Stories: ${totalEpicStories}`} />
                  <Chip label={`Standalone Stories: ${standaloneStories.length}`} />
                </Stack>
              </Box>

              <Box>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1">Epics</Typography>
                  <Button size="small" startIcon={<Add />} onClick={handleAddEpic}>
                    Add Epic
                  </Button>
                </Stack>
                  <Stack spacing={1.5}>
                    {epics.map((epic, index) => (
                      <Card
                        key={`epic-${index}`}
                        variant="outlined"
                        sx={{ 
                          borderLeft: (theme) => `4px solid ${
                            epic.source === 'jira' && epic.jira_key 
                              ? theme.palette.info.main 
                              : theme.palette.primary.main
                          }`
                        }}
                      >
                        <CardContent sx={{ p: 1.5 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle2">Epic {index + 1}</Typography>
                            {epic.source === 'jira' && epic.jira_key && (
                              <>
                                <Chip
                                  size="small"
                                  label={`Editing ${epic.jira_key}`}
                                  color="info"
                                  sx={{ height: 20 }}
                                />
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={buildJiraTicketUrl(epic.jira_key)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  sx={{ p: 0.25 }}
                                  aria-label={`Open ${epic.jira_key} in Jira`}
                                >
                                  <OpenInNew fontSize="small" />
                                </IconButton>
                              </>
                            )}
                            <Tooltip title="Move up">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveItem(epics, setEpics, index, 'up')}
                                  disabled={index === 0}
                                >
                                  <ArrowUpward fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move down">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleMoveItem(epics, setEpics, index, 'down')}
                                  disabled={index === epics.length - 1}
                                >
                                  <ArrowDownward fontSize="inherit" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove epic">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => setEpics((prev) => prev.filter((_, idx) => idx !== index))}
                              >
                                <Delete fontSize="inherit" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                            {(() => {
                              const epicPath = `epics[${index}]`;
                              const titleIssue = getFieldIssue(epicPath, 'title');
                              const descriptionIssue = getFieldIssue(epicPath, 'description');
                              return (
                                <>
                                  <TextField
                                    label="Title"
                                    value={epic.title}
                                    onChange={(event) =>
                                      setEpics((prev) =>
                                        prev.map((item, idx) =>
                                          idx === index ? { ...item, title: event.target.value } : item
                                        )
                                      )
                                    }
                                    fullWidth
                                    error={!!titleIssue?.error}
                                    helperText={titleIssue?.error || titleIssue?.warning || undefined}
                                  />
                                  <TextField
                                    label="Description"
                                    value={epic.description}
                                    onChange={(event) =>
                                      setEpics((prev) =>
                                        prev.map((item, idx) =>
                                          idx === index ? { ...item, description: event.target.value } : item
                                        )
                                      )
                                    }
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    error={!!descriptionIssue?.error}
                                    helperText={descriptionIssue?.error || descriptionIssue?.warning || undefined}
                                  />
                                </>
                              );
                            })()}
                            <Autocomplete
                              freeSolo
                              options={assigneeOptions}
                              value={epic.assignee_display_name || ''}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.display_name || '';
                              }}
                              onChange={(_event, value) => {
                                if (typeof value === 'string') {
                                  setEpics((prev) =>
                                    prev.map((item, idx) =>
                                      idx === index
                                        ? {
                                            ...item,
                                            assignee_display_name: value,
                                            assignee_account_id: '',
                                          }
                                        : item
                                    )
                                  );
                                } else if (value) {
                                  setEpics((prev) =>
                                    prev.map((item, idx) =>
                                      idx === index
                                        ? {
                                            ...item,
                                            assignee_display_name: value.display_name,
                                            assignee_account_id: value.account_id || '',
                                          }
                                        : item
                                    )
                                  );
                                }
                              }}
                              onInputChange={(_event, value) => {
                                const epicKey = `epic-${index}`;
                                setAssigneeSearchQuery((prev) => ({ ...prev, [epicKey]: value }));
                                if (value.length >= 2) {
                                  handleSearchAssignees(epicKey, value);
                                }
                                setEpics((prev) =>
                                  prev.map((item, idx) =>
                                    idx === index ? { ...item, assignee_display_name: value } : item
                                  )
                                );
                              }}
                              inputValue={assigneeSearchQuery[`epic-${index}`] || ''}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Assignee"
                                  fullWidth
                                  helperText="Type to search assignable users or enter manually"
                                  InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                      <>
                                        {assigneeSearchLoading[`epic-${index}`] ? (
                                          <CircularProgress color="inherit" size={18} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                      </>
                                    ),
                                  }}
                                />
                              )}
                            />
                          </Stack>
                          <Divider sx={{ my: 1.5 }} />
                          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                            <Typography variant="subtitle2">Stories</Typography>
                            <Button size="small" startIcon={<Add />} onClick={() => handleAddStoryToEpic(index)}>
                              Add Story
                            </Button>
                          </Stack>
                          <Stack spacing={1.5}>
                            {epic.stories.map((story, storyIndex) => (
                              <Card
                                key={`epic-${index}-story-${storyIndex}`}
                                variant="outlined"
                                sx={{ 
                                  ml: 2, 
                                  borderLeft: (theme) => `4px solid ${
                                    story.source === 'jira' && story.jira_key 
                                      ? theme.palette.info.main 
                                      : theme.palette.secondary.main
                                  }`
                                }}
                              >
                                <CardContent sx={{ p: 1.5 }}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="subtitle2">Story {storyIndex + 1}</Typography>
                                    {story.source === 'jira' && story.jira_key && (
                                      <>
                                        <Chip
                                          size="small"
                                          label={`Editing ${story.jira_key}`}
                                          color="info"
                                          sx={{ height: 20 }}
                                        />
                                        <IconButton
                                          size="small"
                                          component="a"
                                          href={buildJiraTicketUrl(story.jira_key)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          sx={{ p: 0.25 }}
                                          aria-label={`Open ${story.jira_key} in Jira`}
                                        >
                                          <OpenInNew fontSize="small" />
                                        </IconButton>
                                      </>
                                    )}
                                    <Tooltip title="Move up">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updated = [...epic.stories];
                                            [updated[storyIndex - 1], updated[storyIndex]] = [
                                              updated[storyIndex],
                                              updated[storyIndex - 1],
                                            ];
                                            setEpics((prev) =>
                                              prev.map((item, idx) =>
                                                idx === index ? { ...item, stories: updated } : item
                                              )
                                            );
                                          }}
                                          disabled={storyIndex === 0}
                                        >
                                          <ArrowUpward fontSize="inherit" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Move down">
                                      <span>
                                        <IconButton
                                          size="small"
                                          onClick={() => {
                                            const updated = [...epic.stories];
                                            [updated[storyIndex + 1], updated[storyIndex]] = [
                                              updated[storyIndex],
                                              updated[storyIndex + 1],
                                            ];
                                            setEpics((prev) =>
                                              prev.map((item, idx) =>
                                                idx === index ? { ...item, stories: updated } : item
                                              )
                                            );
                                          }}
                                          disabled={storyIndex === epic.stories.length - 1}
                                        >
                                          <ArrowDownward fontSize="inherit" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                    <Tooltip title="Remove story">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          setEpics((prev) =>
                                            prev.map((item, idx) =>
                                              idx === index
                                                ? {
                                                    ...item,
                                                    stories: item.stories.filter((_, sidx) => sidx !== storyIndex),
                                                  }
                                                : item
                                            )
                                          )
                                        }
                                      >
                                        <Delete fontSize="inherit" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                  {renderStoryFields(
                                    story,
                                    (updated) =>
                                      setEpics((prev) =>
                                        prev.map((item, idx) =>
                                          idx === index
                                            ? {
                                                ...item,
                                                stories: item.stories.map((s, sidx) =>
                                                  sidx === storyIndex ? updated : s
                                                ),
                                              }
                                            : item
                                        )
                                      ),
                                    story.jira_key || `epic-${index}-story-${storyIndex}`,
                                    `epics[${index}].stories[${storyIndex}]`
                                  )}
                                </CardContent>
                              </Card>
                            ))}
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                </Box>

              <Box sx={{ mt: 2 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1">Standalone Stories</Typography>
                  <Button size="small" startIcon={<Add />} onClick={handleAddStory}>
                    Add Story
                  </Button>
                </Stack>
                <Stack spacing={1.5}>
                  {standaloneStories.map((story, index) => (
                    <Card
                      key={`standalone-story-${index}`}
                      variant="outlined"
                      sx={{ 
                        borderLeft: (theme) => `4px solid ${
                          story.source === 'jira' && story.jira_key 
                            ? theme.palette.info.main 
                            : theme.palette.secondary.main
                        }`
                      }}
                    >
                      <CardContent sx={{ p: 1.5 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="subtitle2">Story {index + 1}</Typography>
                          {story.source === 'jira' && story.jira_key && (
                            <>
                              <Chip
                                size="small"
                                label={`Editing ${story.jira_key}`}
                                color="info"
                                sx={{ height: 20 }}
                              />
                              <IconButton
                                size="small"
                                component="a"
                                href={buildJiraTicketUrl(story.jira_key)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ p: 0.25 }}
                                aria-label={`Open ${story.jira_key} in Jira`}
                              >
                                <OpenInNew fontSize="small" />
                              </IconButton>
                            </>
                          )}
                          <Tooltip title="Move up">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveItem(standaloneStories, setStandaloneStories, index, 'up')}
                                disabled={index === 0}
                              >
                                <ArrowUpward fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Move down">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveItem(standaloneStories, setStandaloneStories, index, 'down')}
                                disabled={index === standaloneStories.length - 1}
                              >
                                <ArrowDownward fontSize="inherit" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Remove story">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() =>
                                setStandaloneStories((prev) => prev.filter((_, idx) => idx !== index))
                              }
                            >
                              <Delete fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        {renderStoryFields(
                          story,
                          (updated) =>
                            setStandaloneStories((prev) =>
                              prev.map((item, idx) => (idx === index ? updated : item))
                            ),
                          story.jira_key || `standalone-story-${index}`,
                          `standalone_stories[${index}]`
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </Box>

              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="contained"
                  onClick={handleSaveDraft}
                  disabled={isSaving}
                  startIcon={<CheckCircle />}
                >
                  {isSaving ? 'Saving...' : 'Save Draft'}
                </Button>
                {hasEditedTickets && (
                  <Button
                    variant="outlined"
                    onClick={handleCancelEdit}
                    disabled={isSaving || isCommitting}
                    startIcon={<Close />}
                  >
                    Cancel Edit
                  </Button>
                )}
                {isValidating && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Validating…
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {validationResult && (
                <Box>
                  <Alert severity={validationResult.is_valid ? 'success' : 'error'} sx={{ mb: 1.5 }}>
                    {validationResult.is_valid ? 'Draft is valid for commit' : 'Draft has blocking errors'}
                  </Alert>
                  {validationResult.errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 1.5 }}>
                      <Typography variant="subtitle2">Errors</Typography>
                      <List>
                        {validationResult.errors.map((issue, index) => (
                          <ListItem key={`${issue.rule}-${index}`}>
                            <ListItemText primary={issue.message} secondary={issue.item_path} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}
                  {validationResult.warnings.length > 0 && (
                    <Alert severity="warning">
                      <Typography variant="subtitle2">Warnings</Typography>
                      <List>
                        {validationResult.warnings.map((issue, index) => (
                          <ListItem key={`${issue.rule}-${index}`}>
                            <ListItemText primary={issue.message} secondary={issue.item_path} />
                          </ListItem>
                        ))}
                      </List>
                    </Alert>
                  )}
                </Box>
              )}

              <Divider />

              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  Commit Draft
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleCommitDraft}
                    disabled={isCommitting}
                    startIcon={<CheckCircle />}
                  >
                    {isCommitting ? 'Committing...' : 'Commit to Jira'}
                  </Button>
                  <Typography variant="body2" color="text.secondary">
                    Draft ID: {draft.draft_id}
                  </Typography>
                </Stack>
                {commitResult && (
                  <Box sx={{ mt: 1.5 }}>
                    {commitResult.success ? (
                      <Alert severity="success" icon={<CheckCircle />}>
                        Commit succeeded
                      </Alert>
                    ) : (
                      <Alert severity="error" icon={<ErrorIcon />}>
                        Commit failed: {(commitResult as CommitErrorResponse).error}
                      </Alert>
                    )}
                    <Grid container spacing={1.5} sx={{ mt: 1 }}>
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2">Created Epics</Typography>
                            <List>
                              {commitResult.created_epics.map((issue) => (
                                <ListItem key={issue.key}>
                                  <ListItemText
                                    primary={issue.key}
                                    secondary={issue.link}
                                  />
                                </ListItem>
                              ))}
                              {commitResult.created_epics.length === 0 && (
                                <ListItem>
                                  <ListItemText primary="None" />
                                </ListItem>
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card variant="outlined">
                          <CardContent sx={{ p: 1.5 }}>
                            <Typography variant="subtitle2">Created Stories</Typography>
                            <List>
                              {commitResult.created_stories.map((issue) => (
                                <ListItem key={issue.key}>
                                  <ListItemText
                                    primary={issue.key}
                                    secondary={issue.link}
                                  />
                                </ListItem>
                              ))}
                              {commitResult.created_stories.length === 0 && (
                                <ListItem>
                                  <ListItemText primary="None" />
                                </ListItem>
                              )}
                            </List>
                          </CardContent>
                        </Card>
                      </Grid>
                      {commitResult.updated_epics && commitResult.updated_epics.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card variant="outlined">
                            <CardContent sx={{ p: 1.5 }}>
                              <Typography variant="subtitle2">Updated Epics</Typography>
                              <List>
                                {commitResult.updated_epics.map((issue) => (
                                  <ListItem key={issue.key}>
                                    <ListItemText
                                      primary={issue.key}
                                      secondary={issue.link}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                      {commitResult.updated_stories && commitResult.updated_stories.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card variant="outlined">
                            <CardContent sx={{ p: 1.5 }}>
                              <Typography variant="subtitle2">Updated Stories</Typography>
                              <List>
                                {commitResult.updated_stories.map((issue) => (
                                  <ListItem key={issue.key}>
                                    <ListItemText
                                      primary={issue.key}
                                      secondary={issue.link}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                    </Grid>
                    {commitResult.created_links.length > 0 && (
                      <Card variant="outlined" sx={{ mt: 1.5 }}>
                        <CardContent sx={{ p: 1.5 }}>
                          <Typography variant="subtitle2">Links Created</Typography>
                          <List>
                            {commitResult.created_links.map((link, index) => (
                              <ListItem key={`${link.from_key}-${link.to_key}-${index}`}>
                                <ListItemText
                                  primary={`${link.from_key} → ${link.to_key}`}
                                  secondary={link.link_type}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    )}
                    {commitErrors.length > 0 && (
                      <Alert severity="error" sx={{ mt: 1.5 }}>
                        {commitErrors.join('\n')}
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>

              {draftError && <Alert severity="error">{draftError}</Alert>}
            </Stack>
          </CardContent>
        </Card>
                )}
              </Stack>
            </Box>
          </Grid>
        )}
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-description"
        >
          <DialogTitle id="delete-dialog-title">
            Delete {ticketToDelete?.type === 'epic' ? 'Epic' : 'Story'} Ticket
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="delete-dialog-description">
              Are you sure you want to delete <strong>{ticketToDelete?.key}</strong>?
              {ticketToDelete?.hasChildren && (
                <>
                  <br />
                  <br />
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    This Epic has child Stories. All child Stories will be deleted first, then the Epic will be deleted.
                  </Alert>
                </>
              )}
              <br />
              <br />
              This action cannot be undone.
            </DialogContentText>
            {deleteError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {deleteError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={16} /> : <Delete />}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Box>
  );
};
